import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { ApiNetworkError, ApiRequestError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { ProviderButtons } from '../components/ProviderButtons';
import { ThemeModeToggle } from '../components/ThemeModeToggle';

export const LoginPage = () => {
  const { account, signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [revealed, setRevealed] = useState(false);

  if (account) {
    return <Navigate to="/" replace />;
  }

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setIsBusy(true);

    try {
      await signIn(email, password);
      navigate('/');
    } catch (caught) {
      // Never `caught.message` unguarded: a network failure carries the browser's own English string,
      // and showing it here is what made an unreachable API read as a rejected password (R0058).
      if (caught instanceof ApiNetworkError) {
        setError(caught.message);
      } else if (caught instanceof ApiRequestError) {
        setError(caught.message);
      } else {
        // eslint-disable-next-line no-console
        console.error('[bo-admin] Unexpected sign-in failure:', caught);
        setError('Prihlásenie zlyhalo. Skúste to znova.');
      }
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'grid',
        placeItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 2,
        position: 'relative',
        backgroundImage: (t) =>
          `radial-gradient(1100px 520px at 50% -12%, ${t.palette.primary.main}1f, transparent 62%)`,
      }}
    >
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        <ThemeModeToggle />
      </Box>

      <Card variant="outlined" sx={{ width: 408, maxWidth: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2.5 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              <PsychologyOutlinedIcon sx={{ fontSize: 21 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ lineHeight: 1.15 }}>
                MysliKrok
              </Typography>
              <Typography variant="caption" color="text.secondary">
                back office
              </Typography>
            </Box>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.55 }}>
            Prístup majú iba účty s rolou pre správu aplikácie. Rodičovský účet sa sem neprihlási.
          </Typography>

          <form onSubmit={submit}>
            <Stack spacing={2}>
              {error ? (
                <Alert severity="error" variant="outlined">
                  {error}
                </Alert>
              ) : null}
              <TextField
                label="E-mail"
                type="email"
                size="medium"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="username"
                autoFocus
                required
                fullWidth
              />
              <TextField
                label="Heslo"
                type={revealed ? 'text' : 'password'}
                size="medium"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        aria-label={revealed ? 'Skryť heslo' : 'Zobraziť heslo'}
                        aria-pressed={revealed}
                        onClick={() => setRevealed((previous) => !previous)}
                      >
                        {revealed ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button type="submit" variant="contained" size="large" disabled={isBusy}>
                {isBusy ? 'Prihlasujem…' : 'Prihlásiť sa'}
              </Button>

              <ProviderButtons />
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};
